import { useState, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import {
  Input, Label, Select,
} from '@components/common/Forms';
import { useSwrStaticPhoneCountries } from '@hooks/useSwrApi/endpoints/static';
import { RecordInterface } from '@type/form-types';
import { useSwrMutationCheckIdentifier } from '@hooks/useSwrApi/endpoints/company/useSwrMutationCheckIdentifier';

type CompanyForm = (
  Partial<ApiSchema<'BillingDataRead'>> &
  Partial<ApiSchema<'BillingDataWrite'>> &
  { vatNumber?: string }
  );

interface CustomerData {
  customer?:CompanyForm;
  locale: Locale;
  record: RecordInterface;
  isIndividual: boolean;
}


export const CustomerBillingDataForm = (props: CustomerData) => {
  const {
    customer: baseCustomer, locale, record, isIndividual,
  } = props;

  const [customer, setCustomer] = useState<CompanyForm>(baseCustomer ?? {});

  const { t } = useTranslation('common');

  const [countryCode, setCountryCode] = useState<string>(baseCustomer?.phone?.country?.countryCode || '40');
  const [regionCode, setRegionCode] = useState<string>(baseCustomer?.phone?.country?.regionCode || 'RO');
  const [phoneNumber, setPhoneNumber] = useState<string>(baseCustomer?.phone?.number || '');


  useEffect(() => {
    setCustomer(baseCustomer ?? {});
    setCountryCode(baseCustomer?.phone?.country?.countryCode || '40');
    setRegionCode(baseCustomer?.phone?.country?.regionCode || 'RO');
    setPhoneNumber(baseCustomer?.phone?.number || '');
  }, [baseCustomer]);


  const { data: phonePrefixes } = useSwrStaticPhoneCountries(locale);

  const classes = {
    label: 'font-bold text-ink-medium ml-3',
    parent: '!mt-0 mb-3',
  };

  const [companyIdentifier, setCompanyIdentifier] = useState('');
  const { data: companyDataByIdentifier, trigger, error: identifierError } = useSwrMutationCheckIdentifier(
    locale,
    companyIdentifier,
    isIndividual,
  );

  useEffect(() => {
    if (companyIdentifier !== '') {
      void trigger({});
    }
  }, [companyIdentifier, trigger]);

  useEffect(() => {
    if (companyDataByIdentifier
      && Object.keys(companyDataByIdentifier).length > 0) {
      setCustomer((prev) => ({
        ...prev,
        companyName: companyDataByIdentifier.name,
        address: companyDataByIdentifier.address,
        registrationNumber: companyDataByIdentifier.registrationNumber,
      }));
    }
  }, [companyDataByIdentifier]);


  return (
    <>

      <div className=" gap-x-3 py-2 md:grid md:grid-cols-2">
        <Input
          type="hidden"
          value={isIndividual ? 1 : 0}
          {...record('customer.isIndividual', { convertValue: 'toNumber' })}
        />

        <div
          className={`${classes.parent}`}
        >
          <Label
            description={isIndividual
              ? t('page.company.billing.label.cnp')
              : t('page.company.billing.label.identifier')}
            descriptionClass={classes.label}
            required={!isIndividual}
          >
            <Input
              defaultValue={customer?.identifier || ''}
              type="text"
              onBlur={(e) => {
                const cleaned = e.target.value;
                setCompanyIdentifier(cleaned.replace(/\s/g, ''));
              }}
              {...record('customer.vatNumber', {})}
              minLength={6}
              required={!isIndividual}
            />
            {identifierError?.errors[0]?.message
              && <p className="mt-2 text-sm font-semibold text-danger">{identifierError?.errors[0]?.message}</p>}
          </Label>
        </div>

        {!isIndividual
          && (
            <div className={`${classes.parent}`}>
              <Label
                description={t('invoice.label.regnumber')}
                descriptionClass={classes.label}
              >
                <Input
                  defaultValue={customer?.registrationNumber || ''}
                  type="text"
                  {...record('customer.registrationNumber', {})}
                  minLength={6}
                />
              </Label>
            </div>
          )}

        <div
          className={`${classes.parent}`}
        >
          <Label
            descriptionClass={classes.label}
            description={isIndividual
              ? t('page.company.billing.label.name')
              : t('page.company.billing.label.company.name')}
            required
          >
            <Input
              defaultValue={customer?.companyName}
              type="text"
              {...record('customer.companyName', {})}
              required
            />
          </Label>
        </div>

        <div
          className={`${classes.parent}`}
        >
          <Label
            htmlFor="customer.email"
            descriptionClass={classes.label}
            description={t('order.billing.email')}
          >
            <Input
              defaultValue={customer?.mailingEmail || ''}
              type="text"
              {...record('customer.email', {})}
            />
          </Label>
        </div>

        <div className={`${classes.parent}`}>
          <Label
            htmlFor="customer.address"
            descriptionClass={classes.label}
            description={t('order.billing.address')}
            required
          >
            <Input
              defaultValue={customer?.address || ''}
              type="text"
              {...record('customer.address', {})}
              required
            />
          </Label>
        </div>

        <div className={`${classes.parent}`}>
          <Label
            htmlFor="customer.phone"
            description={t('page.company.onboard.form.phone.number')}
            descriptionClass={classes.label}
            required
          >
            <div className="grid grid-cols-5 gap-x-3">
              <div className="col-span-2">
                <Select
                  onChange={(e) => {
                    setCountryCode(e.target.value);
                    setRegionCode(phonePrefixes?.find((item) => item.name === e.target.value)?.id || '');
                  }}
                  value={countryCode}
                >
                  {phonePrefixes && phonePrefixes.map((item) => (
                    <option key={item.id} value={item.name}>
                      {item.id}
                      {' '}
                      ( +
                      {item.name}
                      {' '}
                      )
                    </option>
                  ))}
                </Select>
                <Input
                  type="hidden"
                  value={regionCode}
                />
              </div>
              <div
                className="col-span-3"
              >
                <Input
                  type="text"
                  value={phoneNumber}
                  onChange={(e) => {
                    setPhoneNumber(e.target.value);
                  }}
                  minLength={2}
                  required
                />
              </div>
            </div>
            <Input
              type="hidden"
              value={`+${countryCode}${phoneNumber}`}
              {...record('customer.phone.number', {})}
              required
            />
          </Label>
        </div>
      </div>
    </>

  );
};
